import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCrmCampaigns, previewCrmCampaign, createCrmCampaign, getCrmCampaign, updateCrmCampaign,
  deleteCrmCampaign, uploadCrmCampaignImage, deleteCrmCampaignImage, testCrmCampaign, runCrmCampaign,
  scheduleCrmCampaign, cancelCrmCampaign, getCrmSubscribeLinks, getCrmSubscribers, getOrgBotProfile,
} from '../../services/api';
import { RfmSelect } from './rfmSegments';
import BotPromosPanel from './BotPromosPanel';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatMoney, formatDateTime, plural } from '../../utils/formatters';
import './MailingsTab.css';

const CHANNELS = [
  { key: 'telegram', title: 'Telegram' },
  { key: 'max', title: 'MAX' },
  { key: 'vk', title: 'ВКонтакте' },
];
const CHANNEL_TITLES = Object.fromEntries(CHANNELS.map((c) => [c.key, c.title]));
const STATUS_TITLES = {
  draft: 'Черновик', scheduled: 'Запланирована', running: 'Отправляется', done: 'Отправлена',
  interrupted: 'Прервана', cancelled: 'Остановлена',
};
const STATUS_BADGE = {
  draft: '', scheduled: 'st-risk', running: 'st-risk', done: 'st-active', interrupted: 'st-lost', cancelled: 'st-lost',
};
const GUEST_STATUS = { active: 'Активные', risk: 'Под риском', lost: 'Потерянные' };
const EMPTY = {
  name: '', text: '', button_text: '', button_url: '', channels: ['telegram', 'max', 'vk'],
  filters: { status: '', rfm: '', tag: '', search: '' },
};
const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

/* Готовые шаблоны. «…» — место, которое нужно заполнить своими фактами:
   цены, даты и программу не выдумываем за заведение. button: 'gift' —
   кнопка на подарочные сертификаты из профиля бота, 'phone' — телефон в конце текста. */
const TEMPLATES = [
  {
    key: 'event', title: 'Событие или банный вечер', name: 'Банный вечер — …',
    text: '{имя}, приглашаем на … в … с …:00.\n\n…\n\nКоличество мест ограничено — бронируйте заранее.',
    button: 'phone',
  },
  {
    key: 'winback', title: 'Давно не были', name: 'Возврат гостей — …', filters: { status: 'risk' },
    text: '{имя}, давно вас не видели! Парная натоплена, веники запарены — приходите отдохнуть.\n\n'
      + 'Не забудьте про бонусы: на вашем счёте {баланс}.',
  },
  {
    key: 'bonus', title: 'Напоминание о бонусах', name: 'Бонусы — …',
    text: '{имя}, на вашем счёте {баланс} бонусов. Используйте их при следующем визите — '
      + 'а баланс всегда можно проверить кнопкой «👤 Мой профиль» в этом чате.',
  },
  {
    key: 'gift', title: 'Сертификат к празднику', name: 'Сертификаты — …',
    text: 'Скоро … — а значит, время подарков. Сертификат в баню дарят тем, кому хочется подарить '
      + 'отдых, а не вещь.\n\nОформить можно онлайн за пару минут: сертификат придёт на почту сразу после оплаты.',
    button: 'gift',
  },
  {
    key: 'news', title: 'Новость или изменение графика', name: 'Новость — …',
    text: 'Важное от нас: …\n\nЕсли остались вопросы — звоните, ответим.',
    button: 'phone',
  },
];

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IconSend = () => <Icon><path d="M21 3L10 14" /><path d="M21 3l-7 18-4-7-7-4 18-7z" /></Icon>;
const IconImage = () => <Icon><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="M21 16l-5-5-9 9" /></Icon>;
const IconLink = () => <Icon><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></Icon>;
const IconBack = () => <Icon><path d="M15 6l-6 6 6 6" /></Icon>;

/* ---------- Список подписчиков: кто, где, откуда. Только наша база — ботов не трогает ---------- */

const SUBS_PAGE = 50;
const ANY_SOURCE = '*';

const SubscribersList = ({ sources, onOpenGuests }) => {
  const [channel, setChannel] = useState('');
  const [source, setSource] = useState(ANY_SOURCE);
  const [status, setStatus] = useState('active');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const seq = useRef(0);
  const sourceOptions = [...new Set(sources.map((s) => s.source))];

  useEffect(() => {
    if (searchInput === search) return undefined;
    const timer = setTimeout(() => { setSearch(searchInput); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  useEffect(() => {
    const id = ++seq.current;
    const params = { channel, status, search, limit: SUBS_PAGE, offset: page * SUBS_PAGE };
    if (source !== ANY_SOURCE) params.source = source;
    getCrmSubscribers(params)
      .then((res) => { if (id === seq.current) { setData(res); setError(''); } })
      .catch((err) => { if (id === seq.current) setError(errorOf(err, 'Не удалось загрузить подписчиков')); });
  }, [channel, source, status, search, page]);

  const pick = (setter) => (e) => { setter(e.target.value); setPage(0); };
  const pages = data ? Math.ceil(data.total / SUBS_PAGE) : 0;
  const canOpen = (s) => Boolean(s.phone && onOpenGuests);

  return (
    <div className="mail-subs-list">
      <div className="mail-subs-filters">
        <input type="text" placeholder="Имя, телефон или @ник" value={searchInput}
               onChange={(e) => setSearchInput(e.target.value)} />
        <select value={channel} onChange={pick(setChannel)}>
          <option value="">Все каналы</option>
          {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.title}</option>)}
        </select>
        <select value={source} onChange={pick(setSource)}>
          <option value={ANY_SOURCE}>Любая метка</option>
          {sourceOptions.map((s) => <option key={s || '-'} value={s}>{s || 'без метки'}</option>)}
        </select>
        <select value={status} onChange={pick(setStatus)}>
          <option value="active">Подписаны</option>
          <option value="blocked">Отписались</option>
          <option value="">Все</option>
        </select>
        {data && <span className="mail-dim">{data.total} {plural(data.total, 'гость', 'гостя', 'гостей')}</span>}
      </div>
      {error && <div className="ui-inline-error">{error}</div>}
      <div className="table-wrapper">
        <table>
          <thead><tr><th>Гость</th><th>Канал</th><th>Метка</th><th>Подписался</th><th>Номер</th></tr></thead>
          <tbody>
            {(data?.subscribers || []).map((s) => (
              <tr key={`${s.guest_id}-${s.channel}`} className={canOpen(s) ? 'crm-row' : ''}
                  title={canOpen(s) ? 'Открыть в списке гостей' : undefined}
                  onClick={() => canOpen(s) && onOpenGuests({ search: s.phone })}>
                <td><div className="gb-name">{s.name || '—'}</div><div className="gb-owner">{s.phone}</div></td>
                <td>
                  {CHANNEL_TITLES[s.channel] || s.channel}
                  {s.username && <div className="gb-owner">@{s.username}</div>}
                  {s.status === 'blocked' && <div className="gb-owner">отписался</div>}
                </td>
                <td className="mail-dim">{s.source || '—'}</td>
                <td className="mail-dim">{s.linked_at ? formatDateTime(s.linked_at) : '—'}</td>
                <td className="mail-dim">{s.verified ? 'подтверждён' : 'введён вручную'}</td>
              </tr>
            ))}
            {data && data.subscribers.length === 0 && (
              <tr><td colSpan={5} className="crm-empty">Никого не нашли</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="gb-pager">
          <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>Назад</button>
          <span>{page + 1} из {pages}</span>
          <button type="button" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Вперёд</button>
        </div>
      )}
    </div>
  );
};

/* ---------- Блок «Подписчики»: охват по каналам и ссылки-приглашения ---------- */

const SubscribersPanel = ({ audience, onOpenGuests }) => {
  const [open, setOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [source, setSource] = useState('');
  const [links, setLinks] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const active = audience?.active || {};
  const total = Object.values(active).reduce((a, b) => a + b, 0);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      getCrmSubscribeLinks(source.trim().toLowerCase())
        .then((res) => { if (!cancelled) { setLinks(res.links); setError(''); } })
        .catch((err) => { if (!cancelled) setError(errorOf(err, 'Не удалось получить ссылки')); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, source]);

  const copy = (key, value) => {
    navigator.clipboard?.writeText(value).then(() => { setCopied(key); setTimeout(() => setCopied(''), 1500); });
  };
  const src = source.trim().toLowerCase();

  return (
    <div className="panel mail-subs">
      <div className="mail-subs-head">
        <div>
          <div className="mail-subs-total">{total} <span>{plural(total, 'подписчик', 'подписчика', 'подписчиков')}</span></div>
          <div className="mail-subs-channels">
            {CHANNELS.map((c) => (
              <span key={c.key} className="ui-chip">{c.title}: {active[c.key] || 0}</span>
            ))}
            {audience?.new_week > 0 && <span className="mail-subs-new">+{audience.new_week} за неделю</span>}
          </div>
        </div>
        <div className="mail-subs-actions">
          {total + Object.values(audience?.blocked || {}).reduce((a, b) => a + b, 0) > 0 && (
            <button type="button" className="ui-btn-ghost" onClick={() => setListOpen((v) => !v)}>
              {listOpen ? 'Скрыть список' : 'Список подписчиков'}
            </button>
          )}
          <button type="button" className="ui-btn-ghost" onClick={() => setOpen((v) => !v)}>
            {open ? 'Скрыть' : 'Привлечь подписчиков'}
          </button>
        </div>
      </div>
      {listOpen && <SubscribersList sources={audience?.sources || []} onOpenGuests={onOpenGuests} />}
      {total === 0 && !open && (
        <p className="mail-hint">
          Рассылки приходят только тем, кто подписался на бота заведения. Разместите QR-код на столах,
          стойке и в чеке — гость сканирует, делится номером и попадает в базу рассылок.
        </p>
      )}
      {open && (
        <div className="mail-subs-body">
          <div className="ui-field mail-subs-source">
            <label>Метка источника — чтобы видеть, откуда приходят гости</label>
            <input type="text" placeholder="например: table, check, site" value={source}
                   onChange={(e) => setSource(e.target.value)} maxLength={32} />
          </div>
          {error && <div className="ui-inline-error">{error}</div>}
          {links && Object.keys(links).length === 0 && (
            <div className="mail-hint">Ни один мессенджер не подключён — подключите бота в разделе «Пользователи» → «Интеграции».</div>
          )}
          <div className="mail-subs-links">
            {links && Object.entries(links).map(([channel, url]) => {
              const qs = `channel=${channel}${src ? `&source=${encodeURIComponent(src)}` : ''}`;
              return (
                <div key={channel} className="mail-subs-link">
                  <img src={`/api/crm/subscribe-qr?${qs}`} alt={`QR ${CHANNEL_TITLES[channel]}`} />
                  <div className="mail-subs-link-body">
                    <strong>{CHANNEL_TITLES[channel]}</strong>
                    <code>{url}</code>
                    <div className="mail-subs-link-actions">
                      <button type="button" className="ui-btn-ghost" onClick={() => copy(channel, url)}>
                        {copied === channel ? 'Скопировано' : 'Копировать'}
                      </button>
                      <a className="ui-btn-ghost" href={`/api/crm/subscribe-qr?${qs}&download=1`}>Скачать QR</a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {audience?.sources?.length > 0 && (
            <div className="mail-subs-sources">
              <div className="ui-field-label">Откуда пришли подписчики</div>
              {audience.sources.map((s) => (
                <div key={`${s.channel}-${s.source}`} className="mail-subs-source-row">
                  <span>{CHANNEL_TITLES[s.channel] || s.channel} · {s.source || 'без метки'}</span>
                  <strong>{s.count}</strong>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------- Превью сообщения как в мессенджере ---------- */

const MessagePreview = ({ text, imageUrl, button }) => (
  <div className="mail-phone" aria-label="Как увидит гость">
    <div className="mail-phone-bubble">
      {imageUrl && <img src={imageUrl} alt="" className="mail-phone-image" />}
      <div className="mail-phone-text">{text || <span className="mail-phone-empty">Текст сообщения</span>}</div>
    </div>
    {button?.text && <div className="mail-phone-button">{button.text}</div>}
  </div>
);

/* ---------- Редактор черновика ---------- */

const CampaignEditor = ({ campaignId, isAdmin, rules, onBack, onOpenReport }) => {
  const [form, setForm] = useState(EMPTY);
  const [id, setId] = useState(campaignId || null);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(Boolean(campaignId));
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [busy, setBusy] = useState('');
  const textRef = useRef(null);
  const [botProfile, setBotProfile] = useState(null);

  useEffect(() => { getOrgBotProfile().then((res) => setBotProfile(res.profile)).catch(() => {}); }, []);

  const applyTemplate = (t) => {
    if (form.text.trim() && !window.confirm('Заменить текст рассылки шаблоном?')) return;  // eslint-disable-line no-alert
    let button = { button_text: '', button_url: '' };
    if (t.button === 'gift' && botProfile?.gift_url) button = { button_text: 'Выбрать сертификат', button_url: botProfile.gift_url };
    // Звонок — телефоном в тексте: кнопки под сообщением принимают только http(s).
    const text = t.button === 'phone' && botProfile?.phone ? `${t.text}\n\n📞 ${botProfile.phone}` : t.text;
    set({
      name: form.name.trim() ? form.name : t.name,
      text,
      ...button,
      ...(t.filters ? { filters: { ...form.filters, ...t.filters } } : {}),
    });
  };

  const loadCampaign = useCallback(async (cid) => {
    const res = await getCrmCampaign(cid);
    const c = res.campaign;
    setCampaign(c);
    setForm({
      name: c.name, text: c.text || '', button_text: c.button_text || '', button_url: c.button_url || '',
      channels: c.channels?.length ? c.channels : EMPTY.channels,
      filters: { ...EMPTY.filters, ...(c.filters || {}) },
    });
    setDirty(false);
    return c;
  }, []);

  useEffect(() => {
    if (!campaignId) return;
    loadCampaign(campaignId).finally(() => setLoading(false));
  }, [campaignId, loadCampaign]);

  // Охват и пример текста — с задержкой, пока менеджер печатает.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      previewCrmCampaign({ text: form.text, channels: form.channels, filters: form.filters })
        .then((res) => { if (!cancelled) setPreview(res); })
        .catch((err) => { if (!cancelled) setPreview({ error: errorOf(err, 'Не удалось посчитать охват') }); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.text, form.channels, form.filters]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); setNotice(''); };
  const setFilter = (key, value) => set({ filters: { ...form.filters, [key]: value } });
  const toggleChannel = (key) => set({
    channels: form.channels.includes(key) ? form.channels.filter((c) => c !== key) : [...form.channels, key],
  });

  const insert = (token) => {
    const el = textRef.current;
    const start = el?.selectionStart ?? form.text.length;
    const end = el?.selectionEnd ?? form.text.length;
    const text = form.text.slice(0, start) + token + form.text.slice(end);
    set({ text });
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length); });
  };

  const save = async () => {
    setError('');
    if (!form.name.trim()) { setError('Нужно название рассылки'); return null; }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      let cid = id;
      if (cid) await updateCrmCampaign(cid, payload);
      else { cid = (await createCrmCampaign(payload)).id; setId(cid); }
      await loadCampaign(cid);
      return cid;
    } catch (err) {
      setError(errorOf(err, 'Не удалось сохранить'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const ensureSaved = async () => (dirty || !id ? save() : id);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Картинка больше 5 МБ'); return; }
    const cid = await ensureSaved();
    if (!cid) return;
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(file);
    });
    try { await uploadCrmCampaignImage(cid, dataUrl); await loadCampaign(cid); } catch (err) { setError(errorOf(err, 'Картинка не загрузилась')); }
  };

  const removeImage = async () => {
    try { await deleteCrmCampaignImage(id); await loadCampaign(id); } catch (err) { setError(errorOf(err, 'Не удалось убрать картинку')); }
  };

  const action = async (name, fn) => {
    setBusy(name); setError(''); setNotice('');
    try { await fn(); } catch (err) { setError(errorOf(err, 'Не получилось')); } finally { setBusy(''); }
  };

  const sendTest = () => action('test', async () => {
    const cid = await ensureSaved();
    if (!cid) return;
    const res = await testCrmCampaign(cid, testPhone);
    setNotice(`Тест отправлен в ${CHANNEL_TITLES[res.channel] || res.channel}. Проверьте, как выглядит сообщение.`);
  });

  const runNow = () => action('run', async () => {
    const cid = await ensureSaved();
    if (!cid) return;
    const count = preview?.queued ?? 0;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Отправить рассылку «${form.name}» ${count} ${plural(count, 'гостю', 'гостям', 'гостям')}? Отменить отправленное нельзя.`)) return;
    try {
      await runCrmCampaign(cid);
      onOpenReport(cid);
    } catch (err) {
      const quiet = err?.response?.data?.quiet_until;
      if (quiet) setScheduleAt(quiet.slice(0, 16));
      throw err;
    }
  });

  const schedule = () => action('schedule', async () => {
    const cid = await ensureSaved();
    if (!cid) return;
    const res = await scheduleCrmCampaign(cid, scheduleAt);
    await loadCampaign(cid);
    setNotice(`Запланировано на ${formatDateTime(res.scheduled_at)}.`);
  });

  const unschedule = () => action('unschedule', async () => {
    await scheduleCrmCampaign(id, null);
    await loadCampaign(id);
  });

  const remove = () => action('delete', async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Удалить черновик?')) return;
    await deleteCrmCampaign(id);
    onBack();
  });

  if (loading) return <LoadingSpinner />;

  const scheduled = campaign?.status === 'scheduled';
  const locked = scheduled;
  const imageUrl = campaign?.image_url || null;
  const skipped = Object.entries(preview?.skipped || {});
  const personal = /\{(имя|баланс)\}/.test(form.text);

  return (
    <div className="mail-editor">
      <div className="mail-editor-top">
        <button type="button" className="ui-btn-ghost" onClick={onBack}><IconBack /> Все рассылки</button>
        {scheduled && (
          <div className="mail-scheduled-note">
            Запланирована на {formatDateTime(campaign.scheduled_at)}
            {isAdmin && <button type="button" className="ui-btn-ghost" onClick={unschedule} disabled={busy === 'unschedule'}>Снять с расписания</button>}
          </div>
        )}
      </div>

      <div className="mail-editor-grid">
        <div className="panel mail-editor-form">
          <div className="ui-field">
            <label>Название (видно только вам)</label>
            <input type="text" value={form.name} disabled={locked} placeholder="Например: «Банный вечер — пятница»"
                   onChange={(e) => set({ name: e.target.value })} maxLength={120} />
          </div>

          {!locked && (
            <div className="mail-templates">
              <div className="ui-field-label">Начать с шаблона</div>
              <div className="mail-templates-list">
                {TEMPLATES.map((t) => (
                  <button key={t.key} type="button" className="ui-chip" onClick={() => applyTemplate(t)}>{t.title}</button>
                ))}
              </div>
            </div>
          )}

          <div className="ui-field">
            <label>Текст сообщения</label>
            <textarea ref={textRef} rows={7} value={form.text} disabled={locked} maxLength={4000}
                      placeholder="{имя}, в эту пятницу — банный вечер с травяными парениями. Ждём вас с 18:00!"
                      onChange={(e) => set({ text: e.target.value })} />
            <div className="mail-text-tools">
              <button type="button" className="ui-chip" disabled={locked} onClick={() => insert('{имя}')}>+ имя гостя</button>
              <button type="button" className="ui-chip" disabled={locked} onClick={() => insert('{баланс}')}>+ баланс бонусов</button>
              <span className="mail-counter">{form.text.length} / 4000</span>
            </div>
            {form.text.includes('…') && (
              <div className="mail-hint mail-hint-warn">В тексте остались «…» — замените их своими данными перед отправкой.</div>
            )}
            {personal && (
              <div className="mail-hint">
                Подписчики ВКонтакте вводят номер вручную, он не подтверждён — сообщение с именем
                или балансом им не уйдёт. Для них сделайте отдельную рассылку без подстановок.
              </div>
            )}
          </div>

          <div className="mail-attach">
            <div className="ui-field-label"><IconImage /> Картинка</div>
            {imageUrl ? (
              <div className="mail-attach-row">
                <img src={imageUrl} alt="" className="mail-attach-thumb" />
                {!locked && <button type="button" className="ui-btn-ghost" onClick={removeImage}>Убрать</button>}
              </div>
            ) : (
              <label className={`ui-btn-ghost mail-file ${locked ? 'disabled' : ''}`}>
                Загрузить JPG / PNG до 5 МБ
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickImage} disabled={locked} hidden />
              </label>
            )}
          </div>

          <div className="mail-attach">
            <div className="ui-field-label"><IconLink /> Кнопка со ссылкой (необязательно)</div>
            <div className="mail-row">
              <input type="text" placeholder="Надпись: Забронировать" value={form.button_text} disabled={locked}
                     maxLength={40} onChange={(e) => set({ button_text: e.target.value })} />
              <input type="url" placeholder="https://…" value={form.button_url} disabled={locked}
                     onChange={(e) => set({ button_url: e.target.value })} />
            </div>
          </div>

          <div className="mail-attach">
            <div className="ui-field-label">Кому</div>
            <div className="mail-row">
              <select value={form.filters.status} disabled={locked} onChange={(e) => setFilter('status', e.target.value)}>
                <option value="">Все гости</option>
                {Object.entries(GUEST_STATUS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
              <RfmSelect value={form.filters.rfm} onChange={(v) => setFilter('rfm', v)} />
              <input type="text" placeholder="Тег" value={form.filters.tag} disabled={locked}
                     onChange={(e) => setFilter('tag', e.target.value)} />
            </div>
            <div className="mail-channels">
              {CHANNELS.map((c) => (
                <label key={c.key} className={`mail-channel ${form.channels.includes(c.key) ? 'on' : ''}`}>
                  <input type="checkbox" checked={form.channels.includes(c.key)} disabled={locked}
                         onChange={() => toggleChannel(c.key)} />
                  {c.title}
                </label>
              ))}
            </div>
          </div>

          {!locked && (
            <div className="mail-actions">
              {id && <button type="button" className="ui-btn-danger" onClick={remove} disabled={!!busy}>Удалить</button>}
              <button type="button" className="ui-btn-ghost" onClick={save} disabled={saving || !dirty}>
                {saving ? 'Сохраняем…' : dirty || !id ? 'Сохранить черновик' : 'Сохранено'}
              </button>
            </div>
          )}
        </div>

        <div className="mail-side">
          {error && <div className="ui-inline-error">{error}</div>}
          {notice && <div className="ui-inline-success">{notice}</div>}
          <div className="panel mail-side-card">
            <div className="ui-field-label">Как увидит гость</div>
            <MessagePreview text={preview?.sample_text ?? form.text} imageUrl={imageUrl}
                            button={form.button_text && form.button_url ? { text: form.button_text } : null} />
          </div>

          <div className="panel mail-side-card">
            <div className="ui-field-label">Охват</div>
            {preview?.error && <div className="ui-inline-error">{preview.error}</div>}
            {preview && !preview.error && (
              <>
                <div className="mail-reach">
                  <span className="mail-reach-num">{preview.queued}</span>
                  <span>получат сообщение из {preview.matched} гостей в сегменте</span>
                </div>
                <div className="mail-reach-channels">
                  {Object.entries(preview.by_channel || {}).map(([k, v]) => (
                    <span key={k} className="ui-chip">{CHANNEL_TITLES[k] || k}: {v}</span>
                  ))}
                </div>
                {skipped.length > 0 && (
                  <ul className="mail-reach-skipped">
                    {skipped.map(([reason, n]) => <li key={reason}>{preview.labels?.[reason] || reason}: {n}</li>)}
                  </ul>
                )}
              </>
            )}
            <div className="mail-rules">
              Тихие часы {rules?.quiet_start}:00–{rules?.quiet_end}:00, не чаще раза в {rules?.min_gap_days}{' '}
              {plural(rules?.min_gap_days || 0, 'день', 'дня', 'дней')} одному гостю.
            </div>
          </div>

          <div className="panel mail-side-card">
            <div className="ui-field-label">Проверить на себе</div>
            <div className="mail-row">
              <input type="tel" placeholder="Ваш номер, подписанный на бота" value={testPhone}
                     onChange={(e) => setTestPhone(e.target.value)} />
              <button type="button" className="ui-btn-ghost" onClick={sendTest} disabled={!!busy || !testPhone.trim()}>
                {busy === 'test' ? 'Отправляем…' : 'Тест'}
              </button>
            </div>
          </div>

          {isAdmin && !scheduled && (
            <div className="panel mail-side-card mail-launch">
              <button type="button" className="ui-btn-primary" onClick={runNow}
                      disabled={!!busy || !form.text.trim() || !preview?.queued}>
                <IconSend /> {busy === 'run' ? 'Запускаем…' : `Отправить сейчас${preview?.queued ? ` · ${preview.queued}` : ''}`}
              </button>
              <div className="mail-row">
                <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
                <button type="button" className="ui-btn-ghost" onClick={schedule} disabled={!!busy || !scheduleAt || !form.text.trim()}>
                  Запланировать
                </button>
              </div>
            </div>
          )}
          {!isAdmin && (
            <div className="mail-hint">Отправить или запланировать рассылку может администратор.</div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------- Отчёт по отправленной рассылке ---------- */

const CampaignReport = ({ campaignId, isAdmin, progress, onBack, onChanged }) => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('');
  const [resumeError, setResumeError] = useState('');
  const running = progress?.running && progress.campaign_id === campaignId;

  const load = useCallback(() => getCrmCampaign(campaignId, filter).then(setData), [campaignId, filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => { load(); onChanged(); }, 2000);
    return () => clearInterval(t);
  }, [running, load, onChanged]);

  if (!data) return <LoadingSpinner />;
  const { campaign, report, messages, labels } = data;
  const by = report?.by_status || {};
  const sent = by.sent || 0;
  const pct = running && progress.total ? Math.round((progress.checked / progress.total) * 100) : null;
  const resume = async () => {
    setResumeError('');
    try { await runCrmCampaign(campaignId); onChanged(); setTimeout(load, 1500); } catch (err) { setResumeError(errorOf(err, 'Не удалось продолжить')); }
  };

  return (
    <div className="mail-report">
      <div className="mail-editor-top">
        <button type="button" className="ui-btn-ghost" onClick={onBack}><IconBack /> Все рассылки</button>
        {running && isAdmin && (
          <button type="button" className="ui-btn-danger" onClick={() => cancelCrmCampaign(campaignId).then(onChanged)}>
            Остановить отправку
          </button>
        )}
      </div>

      <div className="panel mail-report-head">
        <div>
          <h3>{campaign.name}</h3>
          <div className="mail-dim">
            <span className={`gb-badge ${STATUS_BADGE[campaign.status] || ''}`}>{STATUS_TITLES[campaign.status] || campaign.status}</span>
            {campaign.started_at && <> · начата {formatDateTime(campaign.started_at)}</>}
            {campaign.status === 'scheduled' && campaign.scheduled_at && <> · продолжится {formatDateTime(campaign.scheduled_at)}</>}
          </div>
          {pct !== null && <div className="ui-progress"><span style={{ width: `${pct}%` }} /></div>}
          {campaign.status === 'interrupted' && (
            <div className="mail-stopped">
              <div>{campaign.stop_reason || 'Отправка прервалась (например, перезапуск сервера).'}</div>
              {isAdmin && <button type="button" className="ui-btn-primary" onClick={resume}>Продолжить отправку</button>}
              {resumeError && <div className="ui-inline-error">{resumeError}</div>}
            </div>
          )}
        </div>
        <MessagePreview text={campaign.text} imageUrl={campaign.image_url}
                        button={campaign.button_text ? { text: campaign.button_text } : null} />
      </div>

      <div className="ui-metrics-grid mail-metrics">
        <div className="ui-metric-tile"><div className="ui-metric-label">Отправлено</div><div className="ui-metric-value">{sent}</div></div>
        {['7', '14'].map((d) => (
          <div key={d} className="ui-metric-tile">
            <div className="ui-metric-label">Пришли за {d} дней</div>
            <div className="ui-metric-value">
              {report?.returns?.[d]?.guests ?? 0}
              {sent > 0 && <small> · {Math.round(((report?.returns?.[d]?.guests || 0) / sent) * 100)}%</small>}
            </div>
            <div className="mail-dim">{formatMoney(report?.returns?.[d]?.amount || 0)}</div>
          </div>
        ))}
        <div className="ui-metric-tile">
          <div className="ui-metric-label">Не отправлено</div>
          <div className="ui-metric-value">{Object.entries(by).filter(([k]) => k !== 'sent').reduce((a, [, v]) => a + v, 0)}</div>
        </div>
      </div>
      <p className="mail-hint">
        «Пришли» — гости, у которых после сообщения был визит. Это не значит, что пришли именно из-за рассылки:
        часть пришла бы и так. Возвраты подтягиваются из журнала визитов в течение дня.
      </p>

      <div className="panel gb-table-panel">
        <div className="mail-filter">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">Все исходы</option>
            {Object.keys(by).map((k) => <option key={k} value={k}>{labels?.[k] || k} — {by[k]}</option>)}
          </select>
        </div>
        <div className="table-wrapper">
          <table>
            <thead><tr><th>Гость</th><th>Канал</th><th>Итог</th><th>Когда</th></tr></thead>
            <tbody>
              {(messages || []).map((m) => (
                <tr key={m.guest_id}>
                  <td><div className="gb-name">{m.name || '—'}</div><div className="gb-owner">{m.phone}</div></td>
                  <td>{CHANNEL_TITLES[m.channel] || '—'}</td>
                  <td>
                    <span className={m.status === 'sent' ? 'mail-ok' : m.status === 'failed' ? 'mail-bad' : 'mail-dim'}>
                      {labels?.[m.status] || m.status}
                    </span>
                    {m.detail && m.status === 'failed' && <div className="gb-owner">{m.detail}</div>}
                  </td>
                  <td>{m.sent_at ? formatDateTime(m.sent_at) : '—'}</td>
                </tr>
              ))}
              {(!messages || messages.length === 0) && <tr><td colSpan={4} className="crm-empty">Пока пусто</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ---------- Вкладка ---------- */

const MailingsTab = ({ isAdmin, onOpenGuests }) => {
  const [data, setData] = useState(null);
  const [view, setView] = useState({ mode: 'list' });

  const load = useCallback(() => getCrmCampaigns().then(setData).catch(() => setData({ campaigns: [] })), []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <LoadingSpinner />;

  const back = () => { setView({ mode: 'list' }); load(); };
  if (view.mode === 'edit') {
    return (
      <CampaignEditor campaignId={view.id} isAdmin={isAdmin} rules={data.rules} onBack={back}
                      onOpenReport={(id) => { load(); setView({ mode: 'report', id }); }} />
    );
  }
  if (view.mode === 'report') {
    return <CampaignReport campaignId={view.id} isAdmin={isAdmin} progress={data.progress} onBack={back} onChanged={load} />;
  }

  const open = (c) => setView({ mode: ['draft', 'scheduled'].includes(c.status) && !c.started_at ? 'edit' : 'report', id: c.id });

  return (
    <div className="mail-tab">
      <SubscribersPanel audience={data.audience} onOpenGuests={onOpenGuests} />
      <BotPromosPanel />

      <div className="mail-list-head">
        <h3>Рассылки</h3>
        <button type="button" className="ui-btn-primary" onClick={() => setView({ mode: 'edit' })}>
          <IconSend /> Новая рассылка
        </button>
      </div>

      <div className="panel gb-table-panel">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>Рассылка</th><th>Статус</th><th>Каналы</th><th>Отправлено</th><th>Когда</th></tr>
            </thead>
            <tbody>
              {data.campaigns.map((c) => (
                <tr key={c.id} className="crm-row" onClick={() => open(c)}>
                  <td>
                    <div className="gb-name">{c.name}</div>
                    <div className="gb-owner mail-snippet">{c.text}</div>
                  </td>
                  <td><span className={`gb-badge ${STATUS_BADGE[c.status] || ''}`}>{STATUS_TITLES[c.status] || c.status}</span></td>
                  <td className="mail-dim">{(c.channels?.length ? c.channels : CHANNELS.map((x) => x.key)).map((k) => CHANNEL_TITLES[k]).join(', ')}</td>
                  <td>{c.started_at ? <><strong>{c.sent_count}</strong><span className="mail-dim"> из {c.target_count}</span></> : '—'}</td>
                  <td className="mail-dim">
                    {c.status === 'scheduled' ? formatDateTime(c.scheduled_at) : formatDateTime(c.started_at || c.created_at)}
                  </td>
                </tr>
              ))}
              {data.campaigns.length === 0 && (
                <tr><td colSpan={5} className="crm-empty">Рассылок пока не было — создайте первую</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MailingsTab;
